import * as _ from "lodash";
import * as angular from "angular";
import { CampaignDataProvider } from "./campaign.dp";
import { CheckManager } from "../shared/check-manager";
import { ExportDataProvider } from "./export.dp";
import { Campaign } from "../models/campaign.model";
import { Filter } from "../models/filter.model";
import { House } from "../models/house.model";
import { Organisation } from "../models/organisation.model";
import { HouseService } from "../services/house.service";
import { Group } from "../models/group.model";
import { columnsConstants } from "../utils/constants/columns.constants";
import { HouseColumn } from "../utils/interfaces";

export class HouseDataProvider {
    private $http: any;
    private campaignDataProvider: CampaignDataProvider;
    public exportDataProvider: ExportDataProvider;
    private __app_type: string;
    public checkManager: CheckManager;
    private service: HouseService;
    private $q: any;
    private modelsUtils: any;
    public localStorageService: any;

    public filters: Filter = new Filter();
    private searchPromise: any;
    private selectedStreet: any = null;
    public selectedGroup: any = null;
    public effort: object | null = null;
    public error = false;
    public scrollLoading = false;
    public loading = true;
    public countError = false;
    public houses = [];
    public activeCampaign: Campaign;
    private isLeaderView: boolean;
    public defaultColumns: HouseColumn[] = [
        ...columnsConstants.houseDefaultColumns,
    ];
    public columns: Array<any>;
    public count: number | undefined;

    constructor(
        $http: any,
        CampaignDataProvider: CampaignDataProvider,
        ExportDataProvider: ExportDataProvider,
        __app_type: string,
        HouseService: HouseService,
        $q: any,
        ModelsUtils: any,
        localStorageService: any
    ) {
        this.$q = $q;
        this.service = HouseService;
        this.$http = $http;
        this.campaignDataProvider = CampaignDataProvider;
        this.exportDataProvider = ExportDataProvider;
        this.__app_type = __app_type;
        this.checkManager = new CheckManager(this.houses);
        this.activeCampaign = this.campaignDataProvider.getActiveCampaign();
        this.isLeaderView = this.__app_type === "LEADER";
        this.modelsUtils = ModelsUtils;
        this.localStorageService = localStorageService;
    }

    create(house: House): Promise<any> {
        const deferred = this.$q.defer();
        this.service.create(house).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    info(hid: number): Promise<any> {
        const deferred = this.$q.defer();
        this.service.info({ id: hid }).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                let house = this.modelsUtils.house(json.data);
                for (let i: number = 0; i < house.occupants.length; i++) {
                    house.occupants[i] = this.modelsUtils.houseOccupant(
                        house.occupants[i]
                    );
                }
                deferred.resolve(house);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    setColumns(): void {
        this.columns =
            this.localStorageService && this.localStorageService.houseColumns
                ? this.localStorageService.houseColumns
                : this.defaultColumns;

        angular.forEach(this.defaultColumns, (defaultColumn: HouseColumn) => {
            let found = false;
            angular.forEach(this.columns, (column: any) => {
                if (defaultColumn.id == column.id) {
                    found = true;
                }
            });
            if (!found) {
                this.columns.push(defaultColumn);
            }
        });
    }

    sortHousesIntoFloors(houses: House[]): {} {
        let floorHouses = _.groupBy(houses, (house: House) => house.hfloor);

        return Object.keys(floorHouses)
            .sort()
            .reduce((obj: {}, key: string) => {
                obj[key] = floorHouses[key];
                return obj;
            }, {});
    }

    getColumns(): Promise<any> {
        const deferred = this.$q.defer();
        if (this.isLeaderView) {
            this.campaignDataProvider
                .getOrganisations()
                .then((organisation: Organisation) => {
                    this.localStorageService = organisation.getLocalStorage();
                    this.setColumns();
                    deferred.resolve(this.columns);
                });
        } else {
            this.localStorageService = this.activeCampaign.getLocalStorage();
            this.setColumns();
            deferred.resolve(this.columns);
        }

        return deferred.promise;
    }

    clearAll(): void {
        this.filters = new Filter();
        this.searchPromise = null;
        this.houses.splice(0, this.houses.length);
        this.count = undefined;
    }

    setFilters(setFilters: Filter, street?: string, group?: object): void {
        if (street) {
            this.selectedStreet = street;
        } else {
            this.selectedStreet = null;
        }
        if (group) {
            this.selectedGroup = group;
        } else {
            this.selectedGroup = null;
        }
        this.filters = setFilters;
        this.filters.removeSelectedUsers();
        this.resetItems();
    }

    getAll(offset: number, scroll?: boolean): Promise<any> {
        if (
            this.effort &&
            Object.keys(this.filters).length &&
            (!angular.isDefined(this.count) ||
                !this.count ||
                this.houses.length < this.count)
        ) {
            this.error = false;

            if (scroll) this.scrollLoading = true;
            else this.loading = true;

            const deferred = this.$q.defer();
            if (this.searchPromise) {
                this.searchPromise.resolve("user cancelled");
            }
            this.searchPromise = this.$q.defer();

            let activeFilters = this.filters.nonEmptyValues();
            if (this.selectedStreet) {
                activeFilters["street"] = this.selectedStreet;
            }
            if (this.selectedGroup) {
                activeFilters["selected_group"] = this.selectedGroup.gid;
            }

            let last = null;
            if (offset) {
                last = {
                    hid: offset,
                };
            }

            this.$http({
                url: "/api/house/all",
                method: "GET",
                params: {
                    filters: JSON.stringify(activeFilters),
                    effort: this.effort,
                    last: last,
                },
                timeout: this.searchPromise.promise,
            }).then(
                (response: object) => {
                    const json = response["data"];

                    if (!last) {
                        this.houses.splice(0, this.houses.length);
                    }
                    if (json.success) {
                        if (!scroll) {
                            this.getCount();
                        }

                        for (let i = 0; i < json.data.length; i++) {
                            let house = this.modelsUtils.house(json.data[i]);
                            this.checkManager.checkNewItem(house);
                            this.houses.push(house);
                        }
                        deferred.resolve(this.houses);
                    } else {
                        deferred.reject();
                    }
                    if (scroll) this.scrollLoading = false;
                    else this.loading = false;
                },
                (error: any) => {
                    if (error.status > -1) {
                        this.error = true;

                        if (scroll) this.scrollLoading = false;
                        else this.loading = false;
                    }
                    deferred.reject(error);
                }
            );
            return deferred.promise;
        }
    }

    getCount(): Promise<any> {
        this.countError = false;
        const deferred = this.$q.defer();
        let activeFilters = this.filters.nonEmptyValues();
        if (this.selectedStreet) {
            activeFilters["street"] = this.selectedStreet;
        }
        if (this.selectedGroup) {
            activeFilters["selected_group"] = this.selectedGroup.gid;
        }

        this.$http({
            url: "/api/house/count",
            method: "GET",
            params: {
                filters: JSON.stringify(activeFilters),
                effort: this.effort,
            },
            timeout: this.searchPromise.promise,
        }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.setFilteredHousesCount(parseInt(json.data));
                    deferred.resolve(parseInt(json.data));
                }
            },
            (error: object) => {
                if (error["status"] > -1) {
                    this.countError = true;
                }
                deferred.reject(error);
            }
        );
        return deferred.promise;
    }

    setEffort(newEffort: object): void {
        if (newEffort !== this.effort) {
            this.effort = newEffort;
            this.resetItems();
        }
    }

    getLocationTypes(): any {
        return {
            ROOFTOP: "Extremely Accurate",
            RANGE_INTERPOLATED: "Very Accurate",
            GEOMETRIC_CENTER: "Moderately Accurate",
            APPROXIMATE: "Approximate",
            Manual: "Manual (user input)",
            GPS: "Phone GPS",
        };
    }

    delete(house: House): Promise<any> {
        const deferred = this.$q.defer();
        this.service.delete({ id: house.hid }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.removeFromFilteredHouses(house);
                    deferred.resolve(house);
                } else {
                    deferred.reject(json);
                }
            },
            (response: object) => deferred.reject(response)
        );
        return deferred.promise;
    }

    removeFromFilteredHouses(house: House): boolean {
        angular.forEach(this.houses, (thisHouse: House, index: number) => {
            if (thisHouse.hid == house.hid) {
                this.houses.splice(index, 1);
                this.setFilteredHousesCount(this.count - 1);
                return true;
            }
        });
        return false;
    }

    getFilteredHouseCount(): number {
        return this.count;
    }

    addToFilteredHouses(house: House): void {
        this.houses.unshift(house);
        let houseCount = this.count ? this.count + 1 : 1;
        this.setFilteredHousesCount(houseCount);
    }

    setFilteredHousesCount(housesCount: number): void {
        this.count = housesCount;
        this.checkManager.setTotalCount(this.count);
    }

    resetItems(): void {
        this.setFilteredHousesCount(undefined);
        this.getAll(0, false);
    }

    addGroupToFilter(filter: Filter, group?: Group): Filter {
        let filterCopy = _.cloneDeep(filter);
        if (group) {
            filterCopy.group.values.push(group.gid);
            filterCopy.group.checked = true;
        }
        return filterCopy;
    }

    addStreetToFilter(filter: Filter): Filter {
        let filterCopy = _.cloneDeep(filter);
        filterCopy["street"] = this.selectedStreet;
        return filterCopy;
    }

    bulkExport(exportChoices: object[]): Promise<any> {
        const deferred = this.$q.defer();
        var filterCopy = this.addGroupToFilter(
            this.filters,
            this.selectedGroup
        );
        if (this.selectedStreet) {
            filterCopy = this.addStreetToFilter(filterCopy);
        }
        let exportData = this.checkManager.bulkActionData(
            new Filter(filterCopy)
        );
        exportData["data_type"] = "houses";
        exportData["exportChoices"] = exportChoices;
        exportData["timezone"] =
            Intl.DateTimeFormat().resolvedOptions().timeZone;

        this.exportDataProvider.create(exportData).then(
            (json: any) => {
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    bulkDelete(street: object): Promise<any> {
        var filterCopy = this.addGroupToFilter(
            this.filters,
            this.selectedGroup
        );
        if (this.selectedStreet) {
            filterCopy = this.addStreetToFilter(filterCopy);
        }
        let bulkActionData = this.checkManager.bulkActionData(filterCopy);
        bulkActionData["filters"].street = street;

        const deferred = this.$q.defer();
        this.service.bulk_delete(bulkActionData).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    this.resetItems();
                    this.checkManager.removeCheckedItems();
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (response: object) => {
                deferred.reject(response);
            }
        );
        return deferred.promise;
    }

    deleteHouses(hids: string[]): Promise<any> {
        const deferred = this.$q.defer();
        this.service.deleteHouses({ hids: hids }).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (response: object) => {
                deferred.reject(response);
            }
        );
        return deferred.promise;
    }

    editHouses(data: object): Promise<any> {
        const deferred = this.$q.defer();
        this.service.editHouses(data).then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (response: object) => {
                deferred.reject(response);
            }
        );
        return deferred.promise;
    }

    geocodeStats(): Promise<any> {
        const deferred = this.$q.defer();
        this.service.geocodeStats().then(
            (response: object) => {
                const json = response["data"];
                if (json.success) {
                    deferred.resolve(json.data);
                } else {
                    deferred.reject(json);
                }
            },
            (response: object) => {
                deferred.reject(response);
            }
        );
        return deferred.promise;
    }

    updateHouseIfExist(house: House): void {
        if (this.houses) {
            for (let i = 0; i < this.houses.length; i++) {
                if (this.houses[i].hid == house.hid) {
                    this.houses[i].setData(house);
                }
            }
        }
    }

    building(id: number | string): Promise<any> {
        const deferred = this.$q.defer();
        this.service.building(id).then(
            (response: object) => {
                let json = response["data"];
                if (json.success) {
                    const houses = [];

                    for (let i = 0; i < json.data.length; i++) {
                        let house = this.modelsUtils.house(json.data[i]);
                        houses.push(house);
                    }

                    deferred.resolve(houses);
                } else {
                    deferred.reject(json);
                }
            },
            (error: object) => deferred.reject(error)
        );
        return deferred.promise;
    }

    getHouse(hid: number | string): Promise<any> {
        const deferred = this.$q.defer();
        this.service.info({ id: hid }).then((response: object) => {
            const json = response["data"];
            if (json.success) {
                deferred.resolve(this.modelsUtils.house(json.data));
            }
            deferred.reject();
        });
        return deferred.promise;
    }
}

angular
    .module("vcDataProvider")
    .service("HouseDataProvider", HouseDataProvider);
